import { supabase } from './supabase';
import { generateMapUrl } from './utils';
import { createLogger } from './logger';

const log = createLogger('notification');

// Resend 服務介面
export interface ResendEmailParams {
  to: string;
  subject: string;
  html: string;
  from?: string;
  replyTo?: string;
}

export interface ResendEmailResult {
  success: boolean;
  messageId?: string;
  error?: string;
}

// 檢測模式配置
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const USE_EDGE_FUNCTION = Boolean(SUPABASE_URL);

// Resend Email 服務
export const resendService = {
  // 檢查服務模式
  getMode(): 'edge-function' | 'simulation' {
    return USE_EDGE_FUNCTION ? 'edge-function' : 'simulation';
  },

  // 檢查服務是否可用
  isAvailable(): boolean {
    return USE_EDGE_FUNCTION;
  },

  // 發送單一 Email
  async sendEmail(params: ResendEmailParams): Promise<ResendEmailResult> {
    log.debug('發送 Email 到', params.to, '(模式:', this.getMode(), ')');

    // 驗證 Email 地址格式
    if (!this.isValidEmail(params.to)) {
      return {
        success: false,
        error: `無效的 Email 地址: ${params.to}`
      };
    }

    // 使用 Edge Function 模式
    if (USE_EDGE_FUNCTION) {
      return await this.sendViaEdgeFunction(params);
    }

    // 使用模擬模式
    return await this.simulateEmailSending(params);
  },

  // 透過 Supabase Edge Function 發送
  async sendViaEdgeFunction(params: ResendEmailParams): Promise<ResendEmailResult> {
    try {
      log.debug('調用 Supabase Edge Function: send-email');

      const { data, error } = await supabase.functions.invoke('send-email', {
        body: {
          to: params.to,
          subject: params.subject,
          html: params.html,
          from: params.from,
          replyTo: params.replyTo,
        },
      });

      if (error) {
        log.error('Edge Function 調用失敗:', error);
        return {
          success: false,
          error: error.message || 'Edge Function 調用失敗'
        };
      }

      if (!data || !data.success) {
        log.error('Edge Function 回傳錯誤:', data);
        return {
          success: false,
          error: data?.error || 'Edge Function 執行失敗'
        };
      }

      log.info('Edge Function 發送成功:', data.messageId);
      return {
        success: true,
        messageId: data.messageId
      };

    } catch (error) {
      log.error('Edge Function 呼叫異常:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : '未知錯誤'
      };
    }
  },

  // 批量發送 Email
  async sendBatchEmails(emails: ResendEmailParams[]): Promise<{
    success: number;
    failed: number;
    results: ResendEmailResult[];
  }> {
    log.debug('批量發送', emails.length, '封 Email (模式:', this.getMode(), ')');

    const results: ResendEmailResult[] = [];
    let successCount = 0;
    let failedCount = 0;

    for (const emailParams of emails) {
      try {
        const result = await this.sendEmail(emailParams);
        results.push(result);

        if (result.success) {
          successCount++;
        } else {
          failedCount++;
        }

        // 添加延遲避免 API 限制
        await new Promise(resolve => setTimeout(resolve, 100));

      } catch (error) {
        log.error('批量發送單個 Email 失敗:', error);
        results.push({
          success: false,
          error: error instanceof Error ? error.message : '未知錯誤'
        });
        failedCount++;
      }
    }

    log.info(`批量發送完成: 成功 ${successCount}, 失敗 ${failedCount}`);

    return {
      success: successCount,
      failed: failedCount,
      results
    };
  },

  // 模擬 Email 發送（用於開發和測試）
  async simulateEmailSending(params: ResendEmailParams): Promise<ResendEmailResult> {
    log.debug('模擬發送 Email:');
    log.debug('  收件人:', params.to);
    log.debug('  主旨:', params.subject);
    log.debug('  內容長度:', params.html.length, '字符');

    // 模擬網路延遲
    await new Promise(resolve => setTimeout(resolve, 200));

    // 模擬一些失敗情況（5% 機率）
    if (Math.random() < 0.05) {
      return {
        success: false,
        error: '模擬網路錯誤'
      };
    }

    // 生成模擬的訊息 ID
    const mockMessageId = `mock_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;

    log.info('模擬發送成功:', mockMessageId);

    return {
      success: true,
      messageId: mockMessageId
    };
  },

  // Email 地址驗證
  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  },

  // 時間格式化函數 - 移除秒數
  formatTime(timeString?: string): string {
    if (!timeString) return '請參考邀請函';
    
    // 如果時間包含秒數 (HH:MM:SS)，移除秒數部分
    if (timeString.match(/^\d{2}:\d{2}:\d{2}$/)) {
      return timeString.substring(0, 5); // 保留 HH:MM
    }
    
    return timeString;
  },

  // 智慧時間格式化函數 - 支援多時間段顯示
  formatDetailedTime(params: {
    welcomePartyTime?: string;
    ceremonyTime?: string;
    receptionTime?: string;
    weddingTime?: string;
  }): string {
    const { welcomePartyTime, ceremonyTime, receptionTime, weddingTime } = params;
    
    // 清理時間格式
    const cleanTime = (time?: string) => time ? this.formatTime(time) : null;
    const welcome = cleanTime(welcomePartyTime);
    const ceremony = cleanTime(ceremonyTime);
    const reception = cleanTime(receptionTime);
    const wedding = cleanTime(weddingTime);
    
    // 情況1: 迎賓酒會 + 婚宴
    if (welcome && reception) {
      return `${welcome} 迎賓酒會｜${reception} 開席<br><span style="font-size: 14px; color: #6b7280;">Welcome reception at ${welcome} | Dinner starts at ${reception}</span>`;
    }
    
    // 情況2: 證婚儀式 + 婚宴  
    if (ceremony && reception) {
      return `${ceremony} 證婚儀式｜${reception} 婚宴<br><span style="font-size: 14px; color: #6b7280;">Wedding ceremony at ${ceremony} | Reception at ${reception}</span>`;
    }
    
    // 情況3: 迎賓酒會 + 證婚儀式 + 婚宴
    if (welcome && ceremony && reception) {
      return `${welcome} 迎賓｜${ceremony} 證婚｜${reception} 婚宴<br><span style="font-size: 14px; color: #6b7280;">Welcome at ${welcome} | Ceremony at ${ceremony} | Reception at ${reception}</span>`;
    }
    
    // 情況4: 只有迎賓酒會
    if (welcome && !ceremony && !reception) {
      return `${welcome} 迎賓酒會<br><span style="font-size: 14px; color: #6b7280;">Welcome reception at ${welcome}</span>`;
    }
    
    // 情況5: 只有證婚儀式
    if (ceremony && !welcome && !reception) {
      return `${ceremony} 證婚儀式<br><span style="font-size: 14px; color: #6b7280;">Wedding ceremony at ${ceremony}</span>`;
    }
    
    // 情況6: 只有婚宴
    if (reception && !welcome && !ceremony) {
      return `${reception} 婚宴<br><span style="font-size: 14px; color: #6b7280;">Reception at ${reception}</span>`;
    }
    
    // 回退: 使用主要時間
    if (wedding) {
      return `${wedding}<br><span style="font-size: 14px; color: #6b7280;">Wedding at ${wedding}</span>`;
    }
    
    // 最後回退
    return '請參考邀請函 / Please refer to the invitation';
  },

  // 建立婚禮桌號通知 Email HTML 模板
  createTableNotificationHtml(params: {
    guestName: string;
    tableName: string;
    brideName?: string;
    groomName?: string;
    weddingDate?: string;
    weddingTime?: string;
    welcomePartyTime?: string;
    ceremonyTime?: string;
    receptionTime?: string;
    venue?: string;
    address?: string;
    mapUrl?: string;
  }): string {
    const detailedTime = this.formatDetailedTime({
      welcomePartyTime: params.welcomePartyTime,
      ceremonyTime: params.ceremonyTime,
      receptionTime: params.receptionTime,
      weddingTime: params.weddingTime
    });
    
    // 生成智慧地圖 URL
    const smartMapUrl = generateMapUrl(params.mapUrl, params.venue, params.address);
    
    return `
      <!DOCTYPE html>
      <html lang="zh-TW">
      <head>
        <meta charset="UTF-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>婚禮座位安排通知</title>
      </head>
      <body style="margin: 0; padding: 0; background-color: #fdf2f8; font-family: 'Microsoft JhengHei', 'PingFang TC', 'Helvetica Neue', Arial, sans-serif;">
        <div style="max-width: 600px; margin: 0 auto; padding: 20px;">
          
          <!-- 標題區塊 -->
          <div style="background: linear-gradient(135deg, #ec4899, #f97316); padding: 35px 30px; border-radius: 15px; text-align: center; margin-bottom: 30px; box-shadow: 0 8px 25px rgba(236, 72, 153, 0.3);">
            <h1 style="color: white; margin: 0; font-size: 28px; font-weight: bold; letter-spacing: 1px;">
              💒 婚禮座位安排通知
            </h1>
            <p style="color: rgba(255, 255, 255, 0.9); margin: 10px 0 0 0; font-size: 14px;">
              Wedding Seating Arrangement
            </p>
          </div>
          
          <!-- 主要內容區塊 -->
          <div style="background: white; padding: 35px; border-radius: 15px; box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);">
            
            <!-- 問候語 -->
            <div style="margin-bottom: 25px;">
              <h2 style="color: #ec4899; margin: 0 0 10px 0; font-size: 22px; font-weight: 600;">
                親愛的 ${params.guestName}，
              </h2>
              <p style="font-size: 16px; line-height: 1.6; color: #374151; margin: 0 0 10px 0;">
                感謝您確認參加我們的婚禮！我們很高興能與您分享這個特別的日子。
              </p>
              <h2 style="color: #ec4899; margin: 0 0 15px 0; font-size: 20px; font-weight: 600;">
                Dear ${params.guestName},
              </h2>
              <p style="font-size: 15px; line-height: 1.6; color: #6b7280; margin: 0;">
                Thank you for confirming your attendance at our wedding! We are delighted to share this special day with you.
              </p>
            </div>
            
            <!-- 座位安排區塊 -->
            <div style="background: linear-gradient(135deg, #fef3c7, #fed7aa); padding: 25px; border-radius: 12px; margin: 25px 0; border-left: 5px solid #f59e0b;">
              <h3 style="color: #92400e; margin: 0 0 15px 0; font-size: 18px; font-weight: 600;">
                🍽️ 您的座位安排 / Your Seating Arrangement
              </h3>
              <div style="background: rgba(255, 255, 255, 0.7); padding: 15px; border-radius: 8px; text-align: center;">
                <p style="font-size: 24px; font-weight: bold; color: #92400e; margin: 0 0 8px 0; letter-spacing: 2px;">
                  桌號：${params.tableName}
                </p>
                <p style="font-size: 20px; font-weight: bold; color: #a16207; margin: 0; letter-spacing: 1px;">
                  Table: ${params.tableName}
                </p>
              </div>
            </div>
            
            <!-- 婚禮資訊區塊 -->
            <div style="background: linear-gradient(135deg, #f0f9ff, #e0f2fe); padding: 25px; border-radius: 12px; margin: 25px 0; border-left: 5px solid #0284c7;">
              <h3 style="color: #0369a1; margin: 0 0 20px 0; font-size: 18px; font-weight: 600;">
                📅 婚禮資訊 / Wedding Information
              </h3>
              <div style="space-y: 10px;">
                <div style="display: flex; align-items: center; margin-bottom: 12px;">
                  <span style="display: inline-block; width: 30px; text-align: center; font-size: 16px;">📆</span>
                  <div style="margin-left: 10px;">
                    <div style="color: #374151; font-size: 15px; margin-bottom: 2px;">
                      <strong>日期 / Date：</strong>${params.weddingDate || '請參考邀請函 / Please refer to the invitation'}
                    </div>
                  </div>
                </div>
                <div style="display: flex; align-items: center; margin-bottom: 12px;">
                  <span style="display: inline-block; width: 30px; text-align: center; font-size: 16px;">⏰</span>
                  <div style="margin-left: 10px;">
                    <div style="color: #374151; font-size: 15px; margin-bottom: 2px; line-height: 1.5;">
                      <strong>時間 / Time：</strong>${detailedTime}
                    </div>
                  </div>
                </div>
                <div style="display: flex; align-items: center; margin-bottom: 12px;">
                  <span style="display: inline-block; width: 30px; text-align: center; font-size: 16px;">📍</span>
                  <div style="margin-left: 10px;">
                    <div style="color: #374151; font-size: 15px; margin-bottom: 2px;">
                      <strong>地點 / Venue：</strong>${params.venue || '請參考邀請函 / Please refer to the invitation'}
                    </div>
                    ${smartMapUrl && smartMapUrl !== 'https://maps.google.com' ? `
                    <div style="margin-top: 8px;">
                      <a href="${smartMapUrl}" target="_blank" style="display: inline-block; background: linear-gradient(135deg, #10b981, #059669); color: white; padding: 8px 16px; border-radius: 20px; text-decoration: none; font-size: 13px; font-weight: 500; transition: all 0.3s ease;">
                        📍 在 Google Maps 中查看 | View on Google Maps
                      </a>
                    </div>
                    ` : ''}
                  </div>
                </div>
              </div>
            </div>
            
            <!-- 服裝要求區塊 -->
            <div style="background: linear-gradient(135deg, #f3e8ff, #e0e7ff); padding: 25px; border-radius: 12px; margin: 25px 0; border-left: 5px solid #8b5cf6;">
              <h3 style="color: #6b21a8; margin: 0 0 15px 0; font-size: 18px; font-weight: 600;">
                👔 服裝建議 / Dress Code
              </h3>
              <div style="background: rgba(255, 255, 255, 0.7); padding: 15px; border-radius: 8px;">
                <p style="font-size: 16px; color: #6b21a8; margin: 0 0 8px 0; line-height: 1.6;">
                  請著正式服裝🤵🏻💃🏻，與我們一同留下美好的回憶。
                </p>
                <p style="font-size: 14px; color: #7c3aed; margin: 0; line-height: 1.6;">
                  Formal attire appreciated🤵🏻💃🏻. Let’s make this day unforgettable!
                </p>
              </div>
            </div>
            
            <!-- 溫馨提醒和署名 -->
            <div style="text-align: center; margin-top: 35px; padding-top: 25px; border-top: 2px solid #e5e7eb;">
              <p style="font-size: 16px; line-height: 1.6; color: #86198f; margin: 0 0 8px 0; font-weight: 500;">
                期待與您共度美好時光！❤️
              </p>
              <p style="font-size: 15px; line-height: 1.6; color: #a855f7; margin: 0 0 20px 0; font-weight: 500;">
                Looking forward to celebrating with you! ❤️
              </p>
              <p style="color: #ec4899; font-weight: bold; font-size: 20px; margin: 0; letter-spacing: 1px;">
                ${params.brideName || ''} & ${params.groomName || ''}
              </p>
              <p style="color: #9ca3af; font-size: 14px; margin: 8px 0 0 0; font-style: italic;">
                敬上 / With love
              </p>
            </div>
          </div>
          
          <!-- 頁尾資訊 -->
          <div style="text-align: center; margin-top: 25px; padding: 15px;">
            <p style="color: #9ca3af; font-size: 12px; margin: 0 0 5px 0;">
              此為系統自動發送的通知信件，請勿直接回覆
            </p>
            <p style="color: #9ca3af; font-size: 11px; margin: 0 0 8px 0;">
              This is an automated notification email, please do not reply directly
            </p>
            <p style="color: #9ca3af; font-size: 12px; margin: 0;">
              由 101 Wedding 平台提供技術支援 / Powered by 101wedding.com
            </p>
          </div>
        </div>
      </body>
      </html>
    `;
  }
};

// 預設匯出
export default resendService;